import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail } from 'lucide-react';

export default function TermsPage() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-8 h-8 text-[#C9956C]" />
            <h1 className="text-3xl font-bold text-[var(--theme-text-primary)]">Terms of Service</h1>
          </div>
          <p className="text-[var(--theme-text-secondary)]">Last updated: February 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-8 text-[var(--theme-text-primary)]">
          
          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">1. Acceptance of Terms</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              By accessing or using WANTD (&quot;the Platform&quot;), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Platform. WANTD is operated by WANTD Pty Ltd 
              (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;), an Australian company.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">2. Description of Service</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              WANTD is a local marketplace platform that connects buyers with sellers in their area. 
              Key features include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--theme-text-secondary)]">
              <li>Browse items for sale in your local area</li>
              <li>Want-to-Buy listings for items you&apos;re seeking</li>
              <li>Inventory listings for items you want to sell</li>
              <li>AI-powered matching between wants and inventory</li>
              <li>In-app messaging between users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">3. User Accounts</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              To use WANTD, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--theme-text-secondary)]">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Be at least 18 years of age or have parental consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">4. Fees</h2>
            <div className="space-y-4 text-[var(--theme-text-secondary)]">
              <p>
                WANTD does not charge marketplace fees. The platform is free to use for browsing, listing, and
                connecting with other users. Transactions are completed offline between users at their own discretion.
              </p>
              <p><strong className="text-[var(--theme-text-primary)]">Premium Membership:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Optional paid subscription for enhanced features</li>
                <li>Premium benefits as described on the Premium page</li>
              </ul>
              <p>
                Premium subscription payments are processed securely through Stripe. We do not store your full credit card details.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">5. Free Trial</h2>
            <div className="space-y-4 text-[var(--theme-text-secondary)]">
              <p><strong className="text-[var(--theme-text-primary)]">7-Day Premium Trial:</strong></p>
              <p>
                New users automatically receive a 7-day free trial of WANTD Premium features upon account creation. 
                The trial includes access to all Premium features including the AI Photo Scanner, Smart Matching priority, 
                and 5 Special Interests per week.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>No payment required:</strong> The trial activates automatically when you create an account. No credit card or payment method is required to start or use the trial.</li>
                <li><strong>Trial duration:</strong> The trial lasts for 7 calendar days from the date of account creation.</li>
                <li><strong>Automatic expiration:</strong> When your trial ends, Premium features will become unavailable. Your account will revert to a standard (free) account automatically.</li>
                <li><strong>No auto-billing:</strong> WANTD does not automatically bill you at the end of your trial. If you wish to continue with Premium, you must actively subscribe.</li>
                <li><strong>One trial per user:</strong> Each user is entitled to one free trial. Creating multiple accounts to obtain additional trials is prohibited.</li>
                <li><strong>Feature access:</strong> Trial users have full access to Premium features. Some features may have usage limits as specified in the Help Center.</li>
              </ul>
              <p>
                After your trial ends, you can subscribe to WANTD Premium at any time to continue enjoying Premium features.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">6. Transaction Rules</h2>
            <div className="space-y-4 text-[var(--theme-text-secondary)]">
              <p>
                WANTD facilitates connections between buyers and sellers. All transactions are completed offline
                between users. WANTD does not process payments or handle funds for marketplace transactions.
              </p>
              <p><strong className="text-[var(--theme-text-primary)]">For Buyers (Wanters):</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use messaging to discuss details and arrange meetups with sellers</li>
                <li>Always verify items in person before completing a deal</li>
                <li>You may cancel negotiations using &quot;No Longer a Match&quot; at any time</li>
              </ul>
              <p><strong className="text-[var(--theme-text-primary)]">For Sellers (Owners):</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You control when to accept matches and engage with buyers</li>
                <li>Failure to respond to matches may affect your Trust Score</li>
                <li>Inventory inactive for 30+ days will be hidden until you log in again</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">7. Prohibited Conduct</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              You agree NOT to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--theme-text-secondary)]">
              <li>List prohibited items (weapons, drugs, stolen goods, counterfeit items)</li>
              <li>Provide false or misleading item descriptions</li>
              <li>Engage in fraud, scams, or deceptive practices</li>
              <li>Harass, threaten, or abuse other users</li>
              <li>Create multiple accounts to manipulate the system</li>
              <li>Use automated tools to scrape data or spam users</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">8. Content and Intellectual Property</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              You retain ownership of content you post (photos, descriptions). By posting, you grant WANTD a 
              non-exclusive, worldwide license to use, display, and distribute your content for platform operations.
            </p>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              The WANTD name, logo, and platform design are our intellectual property and may not be used without permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">9. Trust Score and Reviews</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              WANTD calculates Trust Scores based on account activity, verified status, and user interactions.
              Trust Scores are designed to help users make informed decisions but are not guarantees of user reliability.
            </p>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              Reviews must be honest and based on actual interactions. We reserve the right to remove reviews
              that violate our guidelines or appear fraudulent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">10. Disclaimers</h2>
            <div className="space-y-4 text-[var(--theme-text-secondary)]">
              <p>
                <strong className="text-[var(--theme-text-primary)]">Platform Role:</strong> WANTD is a marketplace 
                facilitator, not a party to transactions. We do not inspect, verify, or guarantee items listed.
              </p>
              <p>
                <strong className="text-[var(--theme-text-primary)]">No Warranties:</strong> The Platform is provided 
                &quot;as is&quot; without warranties of any kind. We do not guarantee continuous, error-free service.
              </p>
              <p>
                <strong className="text-[var(--theme-text-primary)]">User Responsibility:</strong> You are responsible 
                for verifying item condition, meeting safely, and completing transactions appropriately.
              </p>
              <div className="p-4 rounded-lg bg-[#DC2626]/5 border border-[#DC2626]/20 mt-4">
                <p className="font-semibold text-[#DC2626] mb-2">⚠️ No Liability for Goods Quality</p>
                <p className="text-sm">
                  WANTD makes <strong>no representations or warranties</strong> regarding the quality, safety, 
                  legality, authenticity, or condition of any items listed on the Platform. All goods are sold 
                  &quot;as-is&quot; by individual sellers. Buyers assume full responsibility for inspecting items 
                  before purchase. WANTD is not liable for any defects, misrepresentations, or damages arising 
                  from purchased goods.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/20">
                <p className="font-semibold text-[#92400E] mb-2">⚠️ No Guarantee for In-Person Meetups</p>
                <p className="text-sm">
                  WANTD <strong>does not guarantee the safety</strong> of in-person meetups or cash transactions. 
                  Users meet at their own risk. We strongly recommend meeting in public places during daylight hours. 
                  WANTD is not responsible for any incidents, theft, fraud, personal injury, or disputes arising 
                  from in-person meetings. Users agree to release WANTD from any claims related to meetup safety.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">11. Limitation of Liability</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              To the maximum extent permitted by Australian law, WANTD shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages arising from your use of the Platform, 
              including but not limited to disputes with other users, failed transactions, or item quality issues.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">12. Dispute Resolution</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              For disputes between users, we encourage direct communication through our messaging system. 
              WANTD may assist in mediation but is not obligated to resolve user disputes.
            </p>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              For disputes with WANTD, Australian law governs these Terms. Any legal proceedings shall be 
              conducted in the courts of Victoria, Australia.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">13. Termination</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              We may suspend or terminate your account for violation of these Terms, fraudulent activity, 
              or at our discretion. You may delete your account at any time through your Profile settings. 
              Upon termination, your right to use the Platform ceases, but these Terms survive as applicable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">14. Changes to Terms</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              We may update these Terms from time to time. Material changes will be notified via email or 
              Platform notification. Continued use after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">15. Contact Us</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              For questions about these Terms, contact us at:
            </p>
            <div className="p-4 rounded-xl bg-[var(--theme-surface)] border border-[var(--theme-border)]">
              <div className="flex items-center gap-2 text-[var(--theme-text-primary)]">
                <Mail className="w-5 h-5 text-[#C9956C]" />
                <a href="mailto:legal@wantd.au" className="text-[#C9956C] hover:underline">legal@wantd.au</a>
              </div>
              <p className="mt-2 text-sm text-[var(--theme-text-secondary)]">
                WANTD Pty Ltd<br />
                Melbourne, Victoria, Australia
              </p>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-[var(--theme-border)]">
          <div className="flex flex-wrap gap-6 text-sm">
            <Link to="/privacy" className="text-[#C9956C] hover:underline">Privacy Policy</Link>
            <Link to="/about-us" className="text-[#C9956C] hover:underline">About Us</Link>
            <Link to="/" className="text-[#C9956C] hover:underline">Back to WANTD</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
